// iPhone photos are often HEIC/HEIF, sometimes even saved with a .jpg/.jpeg name. Browsers, our
// document previews and the backend analysis cannot read HEIC, so document pickers turn them into
// JPEG before the file is staged/uploaded. Everything else passes through untouched.

const HEIC_EXTENSIONS = ['heic', 'heif']

// ISO-BMFF brands used by HEIC/HEIF stills and sequences. AVIF shares the container (and may use
// the generic mif1/msf1 brands), so a file that also lists an AVIF brand is not treated as HEIC.
const HEIC_BRANDS = new Set(['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'mif1', 'msf1'])
const AVIF_BRANDS = new Set(['avif', 'avis'])

const extensionOf = (name = '') => {
    const lastDot = name.lastIndexOf('.')
    return lastDot === -1 ? '' : name.slice(lastDot + 1).toLowerCase()
}

const baseNameOf = (name = '') => {
    const lastDot = name.lastIndexOf('.')
    return lastDot === -1 ? name : name.slice(0, lastDot)
}

// Reads the file's leading "ftyp" box: major brand + compatible brands.
const readBrands = async (file) => {
    const bytes = new Uint8Array(await file.slice(0, 64).arrayBuffer())
    const text = (start) => String.fromCharCode(...bytes.slice(start, start + 4))

    if (bytes.length < 12 || text(4) !== 'ftyp') return null

    const boxSize = ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0
    const end = Math.min(boxSize || bytes.length, bytes.length)

    const compatible = []
    for (let offset = 16; offset + 4 <= end; offset += 4) {
        compatible.push(text(offset))
    }

    return { major: text(8), compatible }
}

// True when the file is an HEIC/HEIF image, judged by its bytes first (the name can lie), then by
// its type or extension.
export const isHeicFile = async (file) => {
    if (!file) return false

    try {
        const brands = await readBrands(file)
        if (brands) {
            const all = [brands.major, ...brands.compatible]
            if (all.some((brand) => AVIF_BRANDS.has(brand))) return false
            if (all.some((brand) => HEIC_BRANDS.has(brand))) return true
        }
    }
    catch (e) {
        // Unreadable header — fall back to the type / name below.
    }

    return ['image/heic', 'image/heif'].includes((file.type || '').toLowerCase())
        || HEIC_EXTENSIONS.includes(extensionOf(file.name))
}

// Converts an HEIC/HEIF file into a JPEG File named "<original name>.jpg". The converter (a few MB)
// is loaded only when it is first needed.
export const convertHeicToJpeg = async (file) => {
    const { heicTo } = await import('heic-to/csp')
    const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 })

    return new File([blob], `${baseNameOf(file.name)}.jpg`, {
        type: 'image/jpeg',
        lastModified: file.lastModified,
    })
}

// Returns the file ready to stage: a JPEG copy when it is HEIC, otherwise the file itself.
// Throws when an HEIC file cannot be converted, so the picker can ask for a JPG/PNG/PDF instead.
export const toUploadableFile = async (file) => {
    if (!(await isHeicFile(file))) return file

    try {
        return await convertHeicToJpeg(file)
    }
    catch (e) {
        throw new Error(`"${file.name}" is an iPhone (HEIC) photo that could not be converted. Please upload it as a .jpg, .png or .pdf file.`)
    }
}

export { HEIC_EXTENSIONS }
